import {
  ActsOnBasis,
  CompanyContractType,
  MutualSettlements,
  OrganizationTypes,
  PartnersStatuses,
  OrganizationStatuses,
  OrganizationIsCert,
} from '@/enums';
import { Filters, SimpleEnumType } from '@/types';

export type DeliveryAddress = {
  id?: number;
  address: string;
  city: string | null;
  street: string | null;
  postal_code: string | null;
  contact_person: string;
  telephone: string;
  template_file?: string | File | null;
  is_main?: boolean
};

export type DeliveryType = {
  id: number | string;
  type: string;
};

export type TransportCompany = {
  id?: number;
  name_transport_company: string;
  inn_transport_company: string;
  telephone: string;
  contract?: string | File | null;
  is_dellin?: boolean;
};

export type CompanyAccount = {
  id: number;
  name_bank: string;
  account: string | number;
  bik: string | number;
};

export type CompanyContract = {
  number_contract: string | number;
  name_contract: string;
  is_active: boolean; // статус договора
  type_of_contract: SimpleEnumType<CompanyContractType>; // вид договора
  mutual_settlements: SimpleEnumType<MutualSettlements>; // ведение взаиморасчётов
  contract_organization_title: string; // наименование ЮЛ, с которым заключён договор
};

export type OrganizationManagerData = {
  full_name: string,
  email: string,
  phone: string,
};

export namespace Organizations {
  export enum Pages {
    ORGANIZATIONS = 'organizations',
    ORGANIZATION_DETAIL = 'organization',
    ORGANIZATION_EDIT = 'organization-edit',
    ORGANIZATION_NEW = 'organization/new',
    PARTNER_COMPANY = 'partner-company',
    PARTNER_COMPANY_EDIT = 'partner-company/edit',
    ORGANIZATION_CHECK = 'organization_check',
    CONTRACTS = 'contracts',
    CONTRACT_DETAIL = 'contract-detail',
  }

  export type Organization = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
    id?: string | number;
    name_organization: string;
    telephone: string;
    inn_organization: string;
    kpp_organization: string;
    city: string;
    partner_status?: PartnersStatuses;
    code?: string; // код организации
    partner_code?: string; // код партнера
    sigma_partner_code?: string; // сигма код партнера
    organization_type: SimpleEnumType<OrganizationTypes> | OrganizationTypes;
    organization_code?: string;
    active_contracts?: number;
    website?: string;
    is_service_contract?: boolean;
    service_contract_expired_date?: boolean;
    service_areas?: Array<string>;
    organization_status: SimpleEnumType<OrganizationStatuses> | OrganizationStatuses; // подтверждена ли организация
    is_cert: boolean;
    ip_scan_file?: string | File | null;
    date_registration_company?: string; // дата регистрации компании
    region?: string;
    federal_district?: string;
    general_manager?: string;
    name_signatory?: string; // ФИО подписанта
    basis_document?: SimpleEnumType<ActsOnBasis> | string; // действует на основании (устав, доверенность,приказ)
    /**
     * тоже самое что и basis_document, но нужно ТОЛЬКО на бэке
     */
    organization_act_type?: SimpleEnumType<ActsOnBasis> | string;
    email_1?: string; // E-mail Для обмена электронными документами
    email_2?: string; // E-mail Для получения лицензий программных продуктов
    email_3?: string; // E-mail Для получения СУБлицензий программных продуктов
    email_4?: string; // E-mail Для получения рассылок
    contact_person?: string; // ФИО контактного лица
    position_of_the_contact_person?: string; // должность контактного лица
    legal_address?: string; // юридический адрес
    actual_address?: string; // фактический адрес
    delivery_addresses?: DeliveryAddress[];
    transport_companies?: TransportCompany[];
    accounts?: CompanyAccount[];
    contracts?: CompanyContract[];
    attorney_file?: string | File | null; // скан доверенности
    manager_dpp?: string; // ответственный менеджер дпп
    manager_ks?: string; // ответственный менеджер кс
    edo_operator?: string | null; // оператор ЭДО
    edo_contact_person?: string | null; // контактное лицо ответственного ЭДО
    edo_telephone?: string | null; // телефон ответственного ЭДО
  };

  export interface UserOrganization {
    [key: string]: unknown;
    id?: number;
    address: string;
    api_token?: string | null;
    api_token_hidden?: string | null;
    name_company: string;
    partner_code: string;
    sigma_partner_code?: string;
    frontol_partner_code?: string;
    telephone: string;
    website?: string;
    status: SimpleEnumType<PartnersStatuses>;
    company_manager: OrganizationManagerData;
    is_cert: boolean;
    company_roles: string;
    zip?: number | null;
    district?: string;
    city?: string;
    street?: string;
    home?: string;
    flat?: string;
    is_resident?: boolean;
    manager_dpp?: string;
    manager_dpp_email?: string;
    has_accredited_models?: boolean;
    external_api_webhook_url?: string | null;
    can_create_external_api_orders: boolean;
  }

  export interface ServiceCenter {
    id?: number;
    name_service_center: string;
    address: string;
    telephone: string;
    email: string;
    website?: string;
  }

  export interface SalePoint {
    id?: number;
    address: string;
    email: string;
    region: string | null;
    city: string | null;
    name_sale_point: string;
    telephone: Array<string>;
    website?: string;
    status?: SimpleEnumType<PartnersStatuses>;
    is_service?: boolean | null; // true - сервис, false - магазин
    is_active?: boolean;
  }

  export interface OrganizationCheck {
    [key: string]: unknown;
    id?: number;
    name_organization: string;
    inn_organization: string;
    is_sert: boolean;
  }

  export interface OrganizationBlackList {
    [key: string]: unknown;
    id?: number;
    name_organization: string;
    inn_organization: string;
    city: string;
    name_company: string;
  }

  export interface ModelAccreditation {
    id: number,
    is_accreditation: boolean,
    name: string,
  }
}

export interface OrganizationBlackListFilters extends Filters {
  [key: string]: unknown,
}

export interface ApiTokenKey {
  [key: string]: unknown,
  api_token?: string | null;
  api_token_hidden?: string | null;
}

export interface ModelAccreditationFilters extends Filters {
  [key: string]: unknown,
  is_only_accredited: boolean,
}

export interface PartnershipCertificate {
  type: string,
  organization_id: number,
  city: string,
}

export interface ModelContractsFilter extends Filters {
  [key: string]: unknown,
  is_only_active: boolean,
}

export interface WebhookLog {
  [key: string]: unknown,
  id: number;
  created_at: string;
  last_sent_at: string | null;
  url: string;
  result: string | null;
  attempts: number;
  is_done: boolean;
}

export interface Contract {
  id: number;
  number_contract: string;
  name_contract: string | null;
  contract_organization_title: string | null;
  type: string;
  pay_type: string;
  is_active: boolean;
  is_deferment_payment: boolean;
  limit: number | null;
  balance_limit: number | null;
  organization_id: number | null;
}
